// Tile-brush manifest schema + URL resolver for the tile-brush renderer.
// A tile brush is a role-keyed pack of texture tiles (Forgotten Adventures
// convention): straights of varying lengths, corners, joints, connectors, and
// endings. The renderer (tile-brush-renderer.js) walks a wall polyline at
// render time, picks tiles by role, and rotates to match orientation.
//
// Authoring rule: every tile is 200 px per grid cell (the
// TILE_TEXTURE_PIXELS_PER_CELL default). A 1x1 tile is 200×200, a 3x1 straight
// is 600×200, etc. Set `texturePixelsPerCell` on the manifest to override.
//
// This module deliberately does NOT carry consumer-specific brush definitions
// (which textures, what dir on disk, how to label them in a UI). Consumers
// (d3-assets builds one from a tile-brush asset's extracted parts) construct
// their own manifests and pass them to the renderer.

export const TILE_TEXTURE_PIXELS_PER_CELL = 200;

/**
 * @typedef {object} Tile
 * @property {string} file
 *   Either a path relative to `brush.dir`, or an absolute URL (anything
 *   containing `://`, e.g. `https://cdn.example.com/foo.png`). When `file`
 *   is an absolute URL, `brush.dir` is ignored.
 * @property {number} cellsW      Tile width in grid cells
 * @property {number} cellsH      Tile height in grid cells
 */

/**
 * A tile brush: an FA-style piece set (Straight / Corner / Joint / Connector /
 * Ending) the renderer picks per-vertex tiles from to compose corners,
 * T-junctions, and crosses along a wall polyline.
 *
 * @typedef {object} TileBrush
 * @property {string} id
 * @property {string} label
 * @property {string} dir
 *   Folder/prefix prepended to each tile's `file` when `file` is a
 *   relative path. Ignored when a tile's `file` is an absolute URL.
 * @property {number} [texturePixelsPerCell]
 *   Authoring resolution of this brush's tiles. Defaults to
 *   TILE_TEXTURE_PIXELS_PER_CELL (200, FA's modular convention).
 * @property {boolean} [useStraightSeamConnector]
 *   Whether to overlay the straight-seam connector (`connectorStraight`,
 *   FA's Connector_A) on internal straight-to-straight seams. Default
 *   (`undefined`) or `true` draws it whenever the piece is present — the
 *   long-standing behavior. An explicit `false` never draws it even when a
 *   Connector_A tile is in the set, letting a "segmented" brush abut its
 *   straights as hard block edges. Lets a consumer store the continuous-vs-
 *   segmented policy on the asset rather than re-deriving it per render.
 * @property {object} pieces
 * @property {Tile[]} pieces.straight
 * @property {Tile[]} [pieces.straightHalf]
 *   1×1 tiles whose art is a half-block (wall material fills only half the
 *   cell, on the outer side away from a vertex). Used to inset the straight
 *   run at a Corner/Joint/Connector node so the vertex piece sits flush in its
 *   own cell instead of overlapping the flanking run (FA's Straight_D).
 *   Optional — a brush without it renders straights full-length up to the node
 *   and the vertex piece overlaps the inner half-cell, the prior behavior.
 * @property {Tile[]} pieces.corner
 * @property {Tile[]} [pieces.joint]
 * @property {Tile[]} [pieces.connector]
 * @property {Tile[]} pieces.ending
 * @property {Tile[]} [pieces.connectorDiag]
 *   1×1 tiles depicting a wall section running diagonally through the
 *   cell. Used for 45° diagonal segments — one tile per step along the
 *   diagonal, rotated to match the segment direction.
 * @property {Tile[]} [pieces.connectorDiagCross]
 *   1×1 cross tile drawn rotated 45° where two diagonals cross, so its X
 *   arms align with the diagonal walls and cover the crossing seam (FA's
 *   inset Joint_A). Distinct from the axis `connector` cross.
 * @property {Tile[]} [pieces.connectorStraight]
 *   1×1 capless chunk overlaid on the seam between two abutting straight
 *   tiles so a "continuous" brush reads as one unbroken run (FA's
 *   Connector_A). Placed centered on each internal straight-to-straight
 *   seam, rotated to the run. Optional — a "segmented" brush omits it and
 *   its straight tiles just abut as hard block edges.
 */

/**
 * Resolve a brush + tile to a fully-qualified URL. If `tile.file` is an
 * absolute URL (contains `://`), it's returned as-is — that's the path
 * d3-assets takes, where the manifest is built dynamically from CDN-served
 * extracted layer URLs. Otherwise the dir prefix is joined to the file.
 *
 * @param {TileBrush} brush
 * @param {Tile} tile
 */
export function tileUrl(brush, tile) {
    if (tile.file.includes('://')) return tile.file;
    return `${brush.dir}/${tile.file}`;
}
